import deburr from "lodash/deburr";
import api from "../services/apiHub";
import getHeaders from "../utils/returnHeadersHub";

export default class CivitatisExperienceRepository {
  autoComplete = async (term) => {
    const headers = getHeaders();
    const normalizedTerm = deburr(term.toLowerCase());

    try {
      const body = { filter: { name: normalizedTerm } };
      const response = await api.post(
        "/api/services/civitatis-experience/auto-complete/",
        body,
        headers,
      );
      const { locations = [], activities = [] } = response.data;
      return { data: { destinations: locations, activities }, headers };
    } catch (error) {
      console.error("Error fetching autocomplete results:", error);
      return { data: { destinations: [], activities: [] }, headers };
    }
  };

  search = async ({ destinationId, filters = {} } = {}) => {
    const headers = getHeaders();

    const filterBody = { ...filters };
    if (destinationId) filterBody.destinationId = destinationId;

    try {
      const response = await api.post(
        "/api/services/civitatis-experience/search/",
        { filter: filterBody },
        headers,
      );
      const data = response.data;
      // API may return a plain array or { activities: [] } — normalise to { activities: [] }
      const activities = Array.isArray(data)
        ? data
        : data?.activities || data?.results || [];
      return { activities };
    } catch (error) {
      console.error("Error fetching search results:", error);
      return { activities: [] };
    }
  };

  getActivity = async ({ activityId, refToken, date } = {}) => {
    const headers = getHeaders();

    const filterBody = { activityId, refToken };
    if (date) filterBody.date = date;

    try {
      const response = await api.post(
        "/api/services/civitatis-experience/get/",
        { filter: filterBody },
        headers,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching activity detail:", error);
      return null;
    }
  };

  checkDiscount = async ({ activityId, refToken, code, cashPrice, cashTotalValue, pointsTotalValue }) => {
    const headers = getHeaders();

    try {
      const response = await api.post(
        "/api/services/civitatis-experience/check-discount/",
        {
          filter: { activityId, refToken },
          couponCode: code,
          cashPrice: cashPrice ?? 0,
          cashTotalValue: cashTotalValue ?? 0,
          pointsTotalValue: pointsTotalValue ?? 0,
        },
        headers,
      );
      return response.data;
    } catch (error) {
      console.error("Error checking discount:", error);
      return null;
    }
  };

  validateCreditCard = async (payload) => {
    const headers = getHeaders();

    try {
      const response = await api.post(
        "/api/services/civitatis-experience/validate-credit-card/",
        { data: payload },
        headers,
      );
      return response.data;
    } catch (error) {
      console.error("Error validating credit card:", error);
      throw error;
    }
  };

  getCheckoutQuestions = async ({ activityId, refToken, date, session, rate, activityTime }) => {
    const headers = getHeaders();

    try {
      const body = {
        filter: {
          activityId,
          refToken,
          date,
          session,
          rate,
          activityTime: activityTime ?? null,
        },
      };
      const response = await api.post(
        "/api/services/civitatis-experience/checkout-questions/",
        body,
        headers,
      );
      return response.data;
    } catch (error) {
      // Regra de negócio (sem vaga, mínimo de passageiros) vem aqui como 400 com
      // a mensagem pronta: engolir o erro deixava o cliente seguir para o checkout.
      console.error("Error fetching checkout questions:", error);
      throw error;
    }
  };

  createBooking = async (payload) => this.booking(payload);

  booking = async (payload) => {
    const headers = getHeaders();

    try {
      const response = await api.post(
        "/api/services/civitatis-experience/booking/",
        payload,
        headers,
      );
      return response.data;
    } catch (error) {
      console.error("Error creating booking:", error);
      throw error;
    }
  };

  getBookingDetails = async (bookingCode) => {
    const headers = getHeaders();

    try {
      const response = await api.get(
        `/api/services/civitatis-experience/booking-details/${bookingCode}`,
        headers,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching booking details:", error);
      return null;
    }
  };

  // Demanda 251 — links do voucher, consultados AO VIVO na Civitatis.
  // Não guardamos os links do nosso lado: o array pode ganhar e-ticket ou
  // ingresso depois do voucher, e um valor gravado ficaria desatualizado.
  getVoucher = async (bookingCode) => {
    const headers = getHeaders();

    try {
      const response = await api.get(
        `/api/services/civitatis-experience/voucher/${bookingCode}`,
        headers,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching voucher:", error);
      return {
        success: false,
        error: "Não foi possível consultar o voucher agora.",
      };
    }
  };

  cancelBooking = async (bookingCode, reason = "") => {
    const headers = getHeaders();

    try {
      const response = await api.post(
        `/api/services/civitatis-experience/cancel-booking/${bookingCode}`,
        { reason },
        headers,
      );
      return response.data;
    } catch (error) {
      console.error("Error cancelling booking:", error);
      throw error;
    }
  };
}
